/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { forwardRef, useState } from 'react'
import { Platform, TextInput as RNTextInput, View } from 'react-native'
import styled, { useTheme } from 'styled-components/native'
import { v4 as uuidv4 } from 'uuid'

import { useHandleFocus } from 'libs/hooks/useHandleFocus'
import { getComputedAccessibilityLabel } from 'shared/accessibility/helpers/getComputedAccessibilityLabel'
import { hiddenFromScreenReader } from 'shared/accessibility/helpers/hiddenFromScreenReader'
import { useMobileFontScaleToDisplay } from 'shared/accessibility/helpers/zoomHelpers'
import { styledButton } from 'ui/components/buttons/styledButton'
import { FlexInputLabel } from 'ui/components/InputLabel/FlexInputLabel'
import { BaseTextInput } from 'ui/components/inputs/BaseTextInput'
import { LabelContainer } from 'ui/components/inputs/LabelContainer'
import {
  getCustomTextInputProps,
  getRNTextInputProps,
  TextInputProps,
} from 'ui/components/inputs/types'
import { Touchable } from 'ui/components/touchable/Touchable'
import { TextInputContainer } from 'ui/designSystem/TextInput/TextInputContainer'
import { ErrorFilled } from 'ui/svg/icons/ErrorFilled'
import { getSpacing, Typo } from 'ui/theme'

const WithRefTextInput: React.ForwardRefRenderFunction<RNTextInput, TextInputProps> = (
  { ...props },
  forwardedRef
) => {
  const { onFocus: onFocusDefault, onBlur: onBlurDefault, isFocus } = useHandleFocus()
  const [textLength, setTextLength] = useState(0)
  const nativeProps = getRNTextInputProps(props)
  const customProps = getCustomTextInputProps(props)
  const textInputID = nativeProps.testID ?? uuidv4()

  const theme = useTheme()
  const Icon = customProps.rightButton?.icon

  function onBlur() {
    onBlurDefault()
    if (nativeProps.onBlur) {
      // @ts-expect-error pass event later when needed
      nativeProps.onBlur()
    }
  }

  function onFocus() {
    onFocusDefault()
    if (nativeProps.onFocus) {
      // @ts-expect-error pass event later when needed
      nativeProps.onFocus()
    }
  }

  const handleChangeText = (value: string) => {
    setTextLength(value.length)
    props.onChangeText?.(value)
  }

  const preventInputFocusOnMouseDown = (event: Event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
  }

  const inputLabel =
    customProps.requiredIndicator === 'symbol' ? `${customProps.label}\u00A0*` : customProps.label

  const computedAccessibilityLabel = getComputedAccessibilityLabel(
    'Champs de texte',
    customProps.label,
    customProps.description,
    customProps.requiredIndicator ? 'Obligatoire' : null,
    customProps.accessibilityHint
  )

  const hiddenFromScreenReaderMobile = Platform.OS === 'web' ? {} : hiddenFromScreenReader()

  const descriptionAndRequired = (
    <React.Fragment>
      <View>
        <Typo.Body style={props.labelStyle}>{inputLabel}</Typo.Body>
        {customProps.description ? <Description>{customProps.description}</Description> : null}
      </View>
      {customProps.requiredIndicator === 'explicit' ? (
        <StyledBodyAccentXs>Obligatoire</StyledBodyAccentXs>
      ) : null}
    </React.Fragment>
  )

  const labels = useMobileFontScaleToDisplay({
    default: descriptionAndRequired,
    at200PercentZoom: <FlexView>{descriptionAndRequired}</FlexView>,
  })

  return (
    <Container>
      <FlexInputLabel htmlFor={textInputID}>
        <LabelContainer {...hiddenFromScreenReaderMobile}>{labels}</LabelContainer>
      </FlexInputLabel>

      <TextInputContainer
        isFocused={isFocus}
        isError={!!customProps.errorMessage}
        isDisabled={customProps.disabled}
        style={customProps.containerStyle}>
        {customProps.leftComponent ? (
          <IconContainer>{customProps.leftComponent}</IconContainer>
        ) : null}
        <BaseTextInput
          {...nativeProps}
          nativeID={textInputID}
          ref={forwardedRef}
          onFocus={onFocus}
          onBlur={onBlur}
          accessibilityLabel={computedAccessibilityLabel}
          accessibilityRequired={!!customProps.requiredIndicator}
          multiline={props.multiline}
          maxLength={customProps.characterCount}
          onChangeText={handleChangeText}
        />
        {customProps.rightButton && Icon ? (
          <IconTouchableOpacity
            testID={customProps.rightButton.testID}
            accessibilityLabel={customProps.rightButton.accessibilityLabel}
            onPress={customProps.rightButton.onPress}
            hitSlop={customProps.rightButton.hitSlop}
            onMouseDown={preventInputFocusOnMouseDown}>
            <Icon size={theme.designSystem.size.icon.m} />
          </IconTouchableOpacity>
        ) : null}
      </TextInputContainer>
      {customProps.errorMessage || customProps.characterCount ? (
        <FooterContainer>
          {customProps.errorMessage ? (
            <ErrorContainer>
              <ErrorIcon />
              <ErrorText>{customProps.errorMessage}</ErrorText>
            </ErrorContainer>
          ) : null}
          {customProps.characterCount ? (
            <CounterContainer>
              <Counter>{`${textLength}/${customProps.characterCount}`}</Counter>
            </CounterContainer>
          ) : null}
        </FooterContainer>
      ) : null}
    </Container>
  )
}

const FlexView = styled.View({
  flexDirection: 'column',
})

export const TextInput = forwardRef<RNTextInput, TextInputProps>(WithRefTextInput)

const Container = styled.View({
  alignItems: 'flex-start',
  width: '100%',
})

const IconTouchableOpacity = styledButton(Touchable)<{ onMouseDown: (event: Event) => void }>({
  maxWidth: getSpacing(15),
})

const StyledBodyAccentXs = styled(Typo.BodyAccentXs)(({ theme }) => ({
  color: theme.designSystem.color.text.subtle,
}))

const Description = styled(StyledBodyAccentXs)(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.xxs,
}))

const IconContainer = styled.View(({ theme }) => ({
  flexShrink: 0,
  marginRight: theme.designSystem.size.spacing.s,
}))

const FooterContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  marginTop: theme.designSystem.size.spacing.xs,
}))

const ErrorContainer = styled.View({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
})

const ErrorText = styled(Typo.BodyAccentS)(({ theme }) => ({
  flex: 1,
  color: theme.designSystem.color.text.error,
  marginLeft: theme.designSystem.size.spacing.xs,
}))

const ErrorIcon = styled(ErrorFilled).attrs(({ theme }) => ({
  color: theme.designSystem.color.icon.error,
  size: theme.designSystem.size.icon.s,
}))({
  flexShrink: 0,
})

const CounterContainer = styled.View({
  marginLeft: 'auto',
  alignItems: 'center',
})

const Counter = styled(Typo.BodyAccentXs)(({ theme }) => ({
  color: theme.designSystem.color.text.subtle,
}))
